import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../supabase";

// Types
export interface CommunityStory {
  id: string;
  title: string;
  content: string;
  image_url?: string;
  author_id: string;
  author_name: string;
  author_avatar?: string;
  likes_count: number;
  created_at: string;
  updated_at: string;
}

// Fetch community stories
const fetchCommunityStories = async (): Promise<CommunityStory[]> => {
  const { data, error } = await supabase
    .from("community_stories")
    .select(
      `
      *,
      author:users!author_id (
        name,
        profile_image_url
      )
    `
    )
    .order("created_at", { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  // Transform the data to include author info
  return (
    data?.map((item) => ({
      ...item,
      author_name: item.author?.name || "Anonymous",
      author_avatar: item.author?.profile_image_url || "",
    })) || []
  );
};

// Create community story
const createCommunityStory = async (
  story: Omit<
    CommunityStory,
    "id" | "created_at" | "updated_at" | "likes_count"
  >
) => {
  const { data, error } = await supabase
    .from("community_stories")
    .insert([story])
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

// Like/unlike story
const toggleStoryLike = async (storyId: string, userId: string) => {
  // Check if user already liked
  const { data: existingLike } = await supabase
    .from("story_likes")
    .select("id")
    .eq("story_id", storyId)
    .eq("user_id", userId)
    .single();

  if (existingLike) {
    // Unlike
    const { error } = await supabase
      .from("story_likes")
      .delete()
      .eq("story_id", storyId)
      .eq("user_id", userId);

    if (error) throw new Error(error.message);
  } else {
    // Like
    const { error } = await supabase
      .from("story_likes")
      .insert([{ story_id: storyId, user_id: userId }]);

    if (error) throw new Error(error.message);
  }

  // Get updated likes count
  const { count } = await supabase
    .from("story_likes")
    .select("*", { count: "exact", head: true })
    .eq("story_id", storyId);

  return count || 0;
};

// Custom hooks
export const useCommunityStories = () => {
  return useQuery({
    queryKey: ["community-stories"],
    queryFn: fetchCommunityStories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
};

export const useCreateCommunityStory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCommunityStory,
    onSuccess: () => {
      // Invalidate and refetch community stories
      queryClient.invalidateQueries({ queryKey: ["community-stories"] });
    },
  });
};

export const useToggleStoryLike = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storyId, userId }: { storyId: string; userId: string }) =>
      toggleStoryLike(storyId, userId),
    onSuccess: (newLikesCount, { storyId }) => {
      // Update the cache with the new likes count
      queryClient.setQueryData(
        ["community-stories"],
        (old: CommunityStory[] | undefined) => {
          if (!old) return [];
          return old.map((story) =>
            story.id === storyId
              ? { ...story, likes_count: newLikesCount }
              : story
          );
        }
      );
    },
  });
};
